import { Component, computed, effect, OnInit, signal } from "@angular/core";
import { ImportWizardStorage } from "../../import-wizard.storage";
import { ImportWizardPendingListItemComponent } from "./components/pending-list-item/import-wizard-pending-list-item.component";
import { CommonModule } from "@angular/common";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { SkeletonComponent } from "src/app/shared/skeleton/skeleton.component";
import { ImportWizardService } from "../../import-wizard.service";
import { ImportWizardPendingStorage } from "./import-wizard-pending-list.storage";
import { MatDialogRef } from "@angular/material/dialog";
import { ImportWizardComponent } from "../../import-wizard.component";
import { ImportWizardPendingService } from "./import-wizard-pending-list.service";
import { JobStatus, PendingImportInterface } from "src/app/dtos/import-wizard/import-wizard.dto";
import { RepositoryService } from "../../../repository.service";
import { SharedImportsService } from "@/app/pages/shared-imports/shared-imports.service";
import { forkJoin } from "rxjs";

enum PendingImportWizardScope {
    ALL = "all",
    SHARED = "shared",
    NOT_SHARED = "not_shared"
}

@Component({
    selector: "app-import-wizard-pending-list",
    templateUrl: "./import-wizard-pending-list.component.html",
    styleUrls: ["./import-wizard-pending-list.component.scss"],
    imports: [
        ImportWizardPendingListItemComponent,
        ScrollingModule,
        CommonModule,
        SkeletonComponent
    ],
    providers: [
        SharedImportsService
    ],
    standalone: true
})
export class ImportWizardPendingListComponent implements OnInit {
    readonly PendingImportWizardScope = PendingImportWizardScope;

    private sharedImports = signal<Record<string, PendingImportInterface[]>>({});
    private notSharedImports = signal<PendingImportInterface[]>([]);
    private allImports = signal<PendingImportInterface[]>([]);

    selectedScope = signal<PendingImportWizardScope>(PendingImportWizardScope.NOT_SHARED);
    searchTerm = signal<string>('');
    loading = signal<boolean>(true);

    _type = computed(() => this.importWizardStorage.type());

    trackById(_: number, item: PendingImportInterface): string {
        return item.id;
    }

    private applyGlobalFilter(imports: PendingImportInterface[]): PendingImportInterface[] {
        return imports.filter(importData =>
            importData.dataset_type === this.importWizardPendingStorage.pendingImportsFilter().dataset_type &&
            importData.repository_id === this.repositoryService.selectedRepository().repository_id
        );
    }

    private baseImports = computed(() => {
        const scope = this.selectedScope();
        if (scope === PendingImportWizardScope.ALL) return this.allImports();
        if (scope === PendingImportWizardScope.NOT_SHARED) return this.notSharedImports();
        return Object.values(this.sharedImports()).flat();
    });

    private filteredImports = computed(() =>
        this.applyGlobalFilter(this.baseImports())
            .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
    );

    sharedCount = computed(() =>
        this.applyGlobalFilter(Object.values(this.sharedImports()).flat()).length
    );
    notSharedCount = computed(() =>
        this.applyGlobalFilter(this.notSharedImports()).length
    );
    allCount = computed(() =>
        this.applyGlobalFilter(this.allImports()).length
    );

    pendingImportItems = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        if (!term) return this.filteredImports();
        return this.applyGlobalFilter(this.allImports())
            .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
            .filter(item => item.dataset_name?.toLowerCase().includes(term));
    });

    constructor(
        private importWizardStorage: ImportWizardStorage,
        private importWizardPendingStorage: ImportWizardPendingStorage,
        private importWizardPendingService: ImportWizardPendingService,
        private importWizardService: ImportWizardService,
        private sharedImportsService: SharedImportsService,
        private repositoryService: RepositoryService,
        private dialogRef: MatDialogRef<ImportWizardComponent>
    ) {
        effect(() => {
            if (!this.loading() && this.allCount() === 0) {
                this.dialogRef.close();
            }
        });
    }

    ngOnInit(): void {
        if (this.importWizardStorage.state().isShared) return;
        this.refetchPendingImports();
    }

    refetchPendingImports(removedId?: string) {
        if (removedId) {
            this.notSharedImports.update(items => items.filter(i => i.id !== removedId));
            this.sharedImports.update(shared => {
                const updated = { ...shared };
                for (const key of Object.keys(updated)) {
                    updated[key] = updated[key].filter(i => i.id !== removedId);
                    if (updated[key].length === 0) delete updated[key];
                }
                return updated;
            });
            this.allImports.update(items => items.filter(i => i.id !== removedId));
        }

        const verifyFn = (job_id: string) => this.sharedImportsService.verifyImportJobIsShared(job_id);
        const dataset_type = this.importWizardPendingStorage.pendingImportsFilter().dataset_type;

        if (dataset_type === "") return;

        forkJoin({
            notShared: this.importWizardPendingService.getAllNotSharedPendingImports(verifyFn, dataset_type),
            shared: this.importWizardPendingService.getAllSharedPendingImports(verifyFn, dataset_type),
        }).subscribe({
            next: ({ notShared, shared }) => {
                const filterRemoved = (items: PendingImportInterface[]) =>
                    removedId ? items.filter(i => i.id !== removedId) : items;

                const filteredNotShared = filterRemoved(notShared);
                const filteredShared = Object.fromEntries(
                    Object.entries(shared).map(([key, items]) => [key, filterRemoved(items)])
                );
                const all = [...filteredNotShared, ...Object.values(filteredShared).flat()];

                this.notSharedImports.set(filteredNotShared);
                this.sharedImports.set(filteredShared);
                this.allImports.set(all);
                this.importWizardPendingStorage.setPendingImports(all);
                this.loading.set(false);
            },
            error: (error) => {
                console.error(error);
                this.loading.set(false);
            }
        });
    }

    search(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.searchTerm.set(target.value);
    }

    handleScopeChange(scope: PendingImportWizardScope) {
        this.selectedScope.set(scope);
    }
}